import { useState, useEffect, useRef } from 'react'
import '../../lib/animations' // inject pulse keyframe
import { useWedding } from '../../context/WeddingContext'
import { supabase } from '../../lib/supabase'
import { Check, ChevronLeft, ChevronRight } from 'lucide-react'

// ── Countdown card styles (swipeable) ────────────────────────
const THEMES = [
  {
    id: 'sunset', name: 'Sunset',
    cardBg: 'linear-gradient(145deg, #7A3D28 0%, #A85A3C 25%, #C4826A 55%, #E8B09A 80%, #F5DED4 100%)',
    ink: 'rgba(255,248,244,0.97)', inkMid: 'rgba(255,240,232,0.82)', inkSoft: 'rgba(255,235,220,0.7)', inkFaint: 'rgba(255,235,220,0.4)',
    accent: 'rgba(245,222,212,0.9)', accentSoft: 'rgba(245,222,212,0.45)', ringStroke: 'rgba(245,222,212,0.3)',
    glow: 'rgba(245,222,212,0.25)', texture: '#5A2010', numberShadow: '0 2px 24px rgba(80,20,5,0.25)', star: 'rgba(255,235,220,0.55)',
    tagBg: 'rgba(255,255,255,0.1)', tagBorder: 'rgba(255,235,220,0.2)', tagInk: 'rgba(255,240,232,0.75)',
    swatch: 'linear-gradient(135deg,#A85A3C,#E8B09A)',
    canvas: {
      stops: [[0, '#7A3D28'], [0.25, '#A85A3C'], [0.55, '#C4826A'], [0.8, '#E8B09A'], [1, '#F5DED4']],
      glow: 'rgba(245,222,212,0.2)', ink: 'rgba(255,248,244,0.95)', inkNum: 'rgba(255,248,244,0.97)', numShadow: 'rgba(80,20,5,0.3)',
      label: 'rgba(255,235,220,0.65)', sub: 'rgba(255,235,225,0.6)', amp: 'rgba(255,248,244,0.5)',
      date: 'rgba(255,240,232,0.85)', venue: 'rgba(255,235,220,0.55)', divider: 'rgba(255,235,220,0.35)',
      tagBg: 'rgba(255,255,255,0.1)', tagInk: 'rgba(255,240,232,0.75)', watermark: 'rgba(255,255,255,0.3)',
    },
  },
  {
    id: 'midnight', name: 'Midnight Gold',
    cardBg: 'linear-gradient(150deg, #14110E 0%, #2B211A 45%, #3A2A20 72%, #181310 100%)',
    ink: 'rgba(250,244,235,0.97)', inkMid: 'rgba(245,235,220,0.82)', inkSoft: 'rgba(212,175,55,0.85)', inkFaint: 'rgba(212,175,55,0.4)',
    accent: 'rgba(212,175,55,0.9)', accentSoft: 'rgba(212,175,55,0.4)', ringStroke: 'rgba(212,175,55,0.35)',
    glow: 'rgba(212,175,55,0.18)', texture: '#C9A84C', numberShadow: '0 2px 28px rgba(212,175,55,0.3)', star: 'rgba(212,175,55,0.7)',
    tagBg: 'rgba(212,175,55,0.1)', tagBorder: 'rgba(212,175,55,0.3)', tagInk: 'rgba(245,235,220,0.85)',
    swatch: 'linear-gradient(135deg,#2B211A,#C9A84C)',
    canvas: {
      stops: [[0, '#14110E'], [0.45, '#2B211A'], [0.72, '#3A2A20'], [1, '#181310']],
      glow: 'rgba(212,175,55,0.16)', ink: 'rgba(250,244,235,0.96)', inkNum: 'rgba(250,244,235,0.98)', numShadow: 'rgba(212,175,55,0.35)',
      label: 'rgba(212,175,55,0.8)', sub: 'rgba(212,175,55,0.7)', amp: 'rgba(212,175,55,0.55)',
      date: 'rgba(245,235,220,0.85)', venue: 'rgba(212,175,55,0.55)', divider: 'rgba(212,175,55,0.4)',
      tagBg: 'rgba(212,175,55,0.12)', tagInk: 'rgba(245,235,220,0.85)', watermark: 'rgba(212,175,55,0.4)',
    },
  },
  {
    id: 'blush', name: 'Blush Petal',
    cardBg: 'linear-gradient(150deg, #FBF1EC 0%, #F7E4DA 40%, #F1D9CE 70%, #EAD3E0 100%)',
    ink: '#5A3326', inkMid: '#7A5345', inkSoft: '#A8755E', inkFaint: 'rgba(122,83,69,0.42)',
    accent: 'rgba(196,130,106,0.85)', accentSoft: 'rgba(196,130,106,0.4)', ringStroke: 'rgba(196,130,106,0.3)',
    glow: 'rgba(255,255,255,0.55)', texture: '#C4826A', numberShadow: '0 2px 18px rgba(196,130,106,0.25)', star: 'rgba(196,130,106,0.6)',
    tagBg: 'rgba(196,130,106,0.1)', tagBorder: 'rgba(196,130,106,0.25)', tagInk: '#8A5A45',
    swatch: 'linear-gradient(135deg,#F7E4DA,#EAD3E0)',
    canvas: {
      stops: [[0, '#FBF1EC'], [0.4, '#F7E4DA'], [0.7, '#F1D9CE'], [1, '#EAD3E0']],
      glow: 'rgba(255,255,255,0.45)', ink: '#5A3326', inkNum: '#7A3D28', numShadow: 'rgba(196,130,106,0.3)',
      label: 'rgba(122,83,69,0.7)', sub: 'rgba(168,117,94,0.85)', amp: 'rgba(168,117,94,0.6)',
      date: '#7A5345', venue: 'rgba(122,83,69,0.6)', divider: 'rgba(122,83,69,0.35)',
      tagBg: 'rgba(196,130,106,0.12)', tagInk: '#8A5A45', watermark: 'rgba(122,83,69,0.4)',
    },
  },
]

function useCountdown(dateStr) {
  const [time, setTime] = useState({})
  useEffect(() => {
    function calc() {
      const diff = new Date(dateStr) - new Date()
      if (diff <= 0) return setTime({ days:0, hours:0, minutes:0, seconds:0, past:true })
      setTime({
        days:    Math.floor(diff / 86400000),
        hours:   Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000)  / 60000),
        seconds: Math.floor((diff % 60000)    / 1000),
        past: false
      })
    }
    calc()
    const t = setInterval(calc, 1000)
    return () => clearInterval(t)
  }, [dateStr])
  return time
}

export default function CountdownPage() {
  const { wedding, events, refreshWedding } = useWedding()
  const time = useCountdown(wedding?.wedding_date)
  const [editDate, setEditDate]     = useState('')
  const [savingDate, setSavingDate] = useState(false)
  const [dateSaved, setDateSaved]   = useState(false)
  const [themeIdx, setThemeIdx]     = useState(0)
  const touchX = useRef(null)

  // Remember the chosen style per couple
  useEffect(() => {
    if (!wedding?.id) return
    const s = localStorage.getItem(`okoiya_cd_theme_${wedding.id}`)
    if (s != null) setThemeIdx(Math.min(Math.max(Number(s) || 0, 0), THEMES.length - 1))
  }, [wedding?.id])

  function pickTheme(i) {
    const n = (i + THEMES.length) % THEMES.length
    setThemeIdx(n)
    try { localStorage.setItem(`okoiya_cd_theme_${wedding?.id}`, String(n)) } catch { /* ignore */ }
  }
  function onTouchStart(e) { touchX.current = e.touches[0].clientX }
  function onTouchEnd(e) {
    if (touchX.current == null) return
    const dx = e.changedTouches[0].clientX - touchX.current
    if (Math.abs(dx) > 40) pickTheme(themeIdx + (dx < 0 ? 1 : -1))
    touchX.current = null
  }

  // Sync input with current wedding date
  if (wedding?.wedding_date && !editDate) {
    setEditDate(wedding.wedding_date.slice(0,10))
  }

  async function saveWeddingDate() {
    if (!editDate || !wedding) return
    setSavingDate(true)
    await supabase.from('weddings').update({ wedding_date: editDate }).eq('id', wedding.id)
    await refreshWedding()
    setDateSaved(true)
    setTimeout(() => setDateSaved(false), 2000)
    setSavingDate(false)
  }

  const [downloading, setDownloading] = useState(false)

  async function downloadCard() {
    setDownloading(true)
    try {
      await document.fonts.ready
      const cv   = THEMES[themeIdx].canvas
      const TX   = THEMES[themeIdx].texture
      const STAR = THEMES[themeIdx].star || cv.divider
      const S = 2, W = 680, H = 1040
      const canvas = document.createElement('canvas')
      canvas.width = W * S; canvas.height = H * S
      const ctx = canvas.getContext('2d')
      ctx.scale(S, S)
      const cx = W / 2
      if (!ctx.roundRect) ctx.roundRect = function(x,y,w,h,r){ this.beginPath(); this.moveTo(x+r,y); this.arcTo(x+w,y,x+w,y+h,r); this.arcTo(x+w,y+h,x,y+h,r); this.arcTo(x,y+h,x,y,r); this.arcTo(x,y,x+w,y,r); this.closePath() }
      const text = (str,x,y,font,color,align='center',ls=0) => {
        ctx.font = font; ctx.fillStyle = color; ctx.textAlign = align
        if ('letterSpacing' in ctx) ctx.letterSpacing = ls ? `${ls}px` : '0px'
        ctx.fillText(str, x, y)
        if ('letterSpacing' in ctx) ctx.letterSpacing = '0px'
      }

      // ── Background gradient ──
      const bg = ctx.createLinearGradient(0, 0, W * 0.45, H)
      cv.stops.forEach(([s,c]) => bg.addColorStop(s, c))
      ctx.beginPath(); ctx.roundRect(0,0,W,H,32); ctx.fillStyle = bg; ctx.fill()
      ctx.save(); ctx.beginPath(); ctx.roundRect(0,0,W,H,32); ctx.clip()   // clip the rest to the card

      // ── Top glow ──
      const glow = ctx.createRadialGradient(cx, 30, 0, cx, 30, 300)
      glow.addColorStop(0, cv.glow); glow.addColorStop(1, 'rgba(255,255,255,0)')
      ctx.fillStyle = glow; ctx.fillRect(0, 0, W, 380)

      // ── Geometric texture overlay (same 60×60 tile + paths as the preview) ──
      const tile = document.createElement('canvas'); tile.width = 60; tile.height = 60
      const tt = tile.getContext('2d')
      tt.fillStyle = TX
      ;[[30,30],[0,0],[60,0],[0,60],[60,60]].forEach(([x,y])=>{ tt.beginPath(); tt.arc(x,y,1.5,0,Math.PI*2); tt.fill() })
      tt.globalAlpha = 0.6
      ;['M30 5 Q35 15 30 25 Q25 15 30 5Z','M30 35 Q35 45 30 55 Q25 45 30 35Z','M5 30 Q15 35 25 30 Q15 25 5 30Z','M35 30 Q45 35 55 30 Q45 25 35 30Z'].forEach(d=>tt.fill(new Path2D(d)))
      ctx.globalAlpha = 0.045; ctx.fillStyle = ctx.createPattern(tile,'repeat'); ctx.fillRect(0,0,W,H); ctx.globalAlpha = 1

      // ── Sunburst ornament ──
      ctx.save(); ctx.translate(cx, 120)
      ctx.strokeStyle = cv.divider; ctx.lineWidth = 1
      ctx.beginPath(); ctx.arc(0,0,26,0,Math.PI*2); ctx.stroke()
      ctx.fillStyle = cv.amp; ctx.beginPath(); ctx.arc(0,0,5,0,Math.PI*2); ctx.fill()
      for (let i=0;i<16;i++){ const a=(i/16)*Math.PI*2, rr=i%2?34:30; ctx.fillStyle = i%2?cv.label:cv.amp; ctx.beginPath(); ctx.arc(Math.cos(a)*rr, Math.sin(a)*rr, i%2?1.5:2.4, 0, Math.PI*2); ctx.fill() }
      ctx.restore()

      // ── "The Wedding of" + couple names ──
      text('THE WEDDING OF', cx, 190, '700 13px Nunito Sans, sans-serif', cv.label, 'center', 4)
      text(p1, cx, 235, '400 58px Gibs, Georgia, serif', cv.ink)
      text('&', cx, 282, 'italic 400 30px Gibs, Georgia, serif', cv.amp)
      text(p2, cx, 332, '400 58px Gibs, Georgia, serif', cv.ink)

      // ── Days number ──
      if (wedding?.wedding_date && !time?.past) {
        ctx.save(); ctx.shadowColor = cv.numShadow; ctx.shadowBlur = 24
        text(String(time?.days ?? 0), cx, 505, '400 150px Gibs, Georgia, serif', cv.inkNum)
        ctx.restore()
        text('DAYS TO GO', cx, 556, '700 15px Nunito Sans, sans-serif', cv.label, 'center', 4)
      } else if (wedding?.wedding_date && time?.past) {
        text('Today is the day', cx, 480, '400 52px Gibs, Georgia, serif', cv.inkNum)
      } else {
        text('Your big day', cx, 470, '400 56px Gibs, Georgia, serif', cv.ink)
        text('is coming soon', cx, 528, '400 38px Gibs, Georgia, serif', cv.sub)
      }

      // ── Star divider ──
      const dy = 622
      ctx.strokeStyle = cv.divider; ctx.lineWidth = 1
      ctx.beginPath(); ctx.moveTo(cx-95, dy); ctx.lineTo(cx-22, dy); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(cx+22, dy); ctx.lineTo(cx+95, dy); ctx.stroke()
      ctx.save(); ctx.translate(cx, dy); ctx.fillStyle = STAR; ctx.beginPath()
      for (let i=0;i<10;i++){ const a=(Math.PI/5)*i - Math.PI/2, rr=i%2?4:9, X=Math.cos(a)*rr, Y=Math.sin(a)*rr; i?ctx.lineTo(X,Y):ctx.moveTo(X,Y) }
      ctx.closePath(); ctx.fill(); ctx.restore()

      // ── Date ──
      let y = 692
      if (wedding?.wedding_date) {
        const dateStr = new Date(wedding.wedding_date).toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'})
        text(dateStr, cx, y, '400 17px Nunito Sans, sans-serif', cv.date); y += 40
      }

      // ── Event tags ──
      if (events?.length > 0) {
        const tags = events.slice(0,3).map(e=>e.type)
        const pad=18, gap=10, hgt=30
        ctx.font = '600 12px Nunito Sans, sans-serif'
        const widths = tags.map(tg => ctx.measureText(tg).width + pad*2)
        const total  = widths.reduce((a,b)=>a+b,0) + gap*(tags.length-1)
        let tx = cx - total/2
        tags.forEach((tag,i)=>{
          const tw = widths[i]
          ctx.fillStyle = cv.tagBg; ctx.beginPath(); ctx.roundRect(tx, y, tw, hgt, 15); ctx.fill()
          ctx.fillStyle = cv.tagInk; ctx.textAlign = 'center'; ctx.font = '600 12px Nunito Sans, sans-serif'
          ctx.fillText(tag, tx + tw/2, y + 19)
          tx += tw + gap
        })
      }

      // ── Watermark ──
      text('MADE WITH OKOIYA', cx, H - 46, '700 12px Nunito Sans, sans-serif', cv.watermark, 'center', 3)
      ctx.restore()

      const link = document.createElement('a')
      link.download = `${p1}-and-${p2}-countdown.png`
      link.href = canvas.toDataURL('image/png')
      document.body.appendChild(link); link.click(); document.body.removeChild(link)
    } catch(e) {
      console.error('Download error:', e)
      alert("Couldn't download — try again.")
    }
    setDownloading(false)
  }

  if (!wedding) return null

  const p1 = wedding.partner1_name
  const p2 = wedding.partner2_name
  const t  = THEMES[themeIdx]

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.5rem' }}>
      <div>
        <h1 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'clamp(1.4rem, 3vw, 1.8rem)', color:'#1A1A1A', margin:'0 0 0.25rem' }}>Countdown & Hype Page</h1>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'#6B6B6B', margin:0 }}>A beautiful shareable page that builds excitement before the big day.</p>
      </div>

      {/* Live Preview — swipeable styles */}
      <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div id="countdown-card" style={{ background:t.cardBg, borderRadius:'1.5rem', padding:'3rem 2rem 2.5rem', textAlign:'center', position:'relative', overflow:'hidden', transition:'background 0.45s ease' }}>

        {/* Subtle geometric texture overlay (skipped during export — html2canvas
            chokes on SVG <pattern> fills; it's near-invisible anyway) */}
        <svg data-html2canvas-ignore="true" style={{ position:'absolute', inset:0, width:'100%', height:'100%', opacity:0.045, pointerEvents:'none' }} preserveAspectRatio="xMidYMid slice">
          <defs>
            <pattern id="tex" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <circle cx="30" cy="30" r="1.5" fill={t.texture}/>
              <circle cx="0" cy="0" r="1.5" fill={t.texture}/>
              <circle cx="60" cy="0" r="1.5" fill={t.texture}/>
              <circle cx="0" cy="60" r="1.5" fill={t.texture}/>
              <circle cx="60" cy="60" r="1.5" fill={t.texture}/>
              <path d="M30 5 Q35 15 30 25 Q25 15 30 5Z" fill={t.texture} opacity="0.6"/>
              <path d="M30 35 Q35 45 30 55 Q25 45 30 35Z" fill={t.texture} opacity="0.6"/>
              <path d="M5 30 Q15 35 25 30 Q15 25 5 30Z" fill={t.texture} opacity="0.6"/>
              <path d="M35 30 Q45 35 55 30 Q45 25 35 30Z" fill={t.texture} opacity="0.6"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#tex)"/>
        </svg>

        {/* Light glow at top */}
        <div style={{ position:'absolute', top:-60, left:'50%', transform:'translateX(-50%)', width:320, height:200, background:`radial-gradient(ellipse, ${t.glow} 0%, transparent 70%)`, pointerEvents:'none' }}/>

        <div style={{ position:'relative', zIndex:2 }}>

          {/* Animated ring ornament */}
          <div style={{ display:'flex', justifyContent:'center', marginBottom:'1.25rem' }}>
            <svg width="64" height="64" viewBox="0 0 64 64" style={{ animation:'spin-slow 12s linear infinite' }}>
              <style>{'@keyframes spin-slow { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }'}</style>
              {[0,30,60,90,120,150,180,210,240,270,300,330].map((deg,i) => (
                <circle key={deg} cx={32 + 26*Math.cos((deg-90)*Math.PI/180)} cy={32 + 26*Math.sin((deg-90)*Math.PI/180)}
                  r={i%3===0?2.5:1.5} fill={i%3===0?t.accent:t.accentSoft}/>
              ))}
              <circle cx="32" cy="32" r="14" fill="none" stroke={t.ringStroke} strokeWidth="1"/>
              <circle cx="32" cy="32" r="5" fill={t.accentSoft}/>
              <circle cx="32" cy="32" r="2.5" fill={t.accent}/>
            </svg>
          </div>

          {/* Couple names */}
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', fontWeight:700, color:t.inkSoft, letterSpacing:'0.22em', textTransform:'uppercase', margin:'0 0 0.6rem' }}>The Wedding of</p>
          <h2 style={{ fontFamily:'Gibs, serif', fontSize:'clamp(1.6rem, 4.5vw, 2.4rem)', color:t.ink, fontWeight:400, lineHeight:1.2, margin:'0 0 0.1rem' }}>{p1}</h2>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:t.inkSoft, margin:'0 0 0.1rem', letterSpacing:'0.15em' }}>&amp;</p>
          <h2 style={{ fontFamily:'Gibs, serif', fontSize:'clamp(1.6rem, 4.5vw, 2.4rem)', color:t.ink, fontWeight:400, lineHeight:1.2, margin:'0 0 2rem' }}>{p2}</h2>

          {/* CENTREPIECE — days number */}
          {wedding?.wedding_date && !time.past && (
            <div style={{ marginBottom:'0.5rem' }}>
              <p style={{ fontFamily:'Gibs, serif', fontSize:'clamp(5rem, 16vw, 9rem)', color:t.ink, margin:0, lineHeight:0.9, fontWeight:400, letterSpacing:'-0.04em',
                textShadow:t.numberShadow }}>
                <span className={(time.days ?? 0) < 10 && (time.days ?? 0) > 0 ? 'pulse-digit' : ''}>
                  {time.days ?? 0}
                </span>
              </p>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:t.inkSoft, margin:'0.5rem 0 0', fontWeight:400, letterSpacing:'0.18em', textTransform:'uppercase' }}>
                days to go
              </p>
            </div>
          )}

          {time.past && (
            <p style={{ fontFamily:'Gibs, serif', fontSize:'2rem', color:t.ink, marginBottom:'1rem' }}>Today is the day ✦</p>
          )}

          {/* Decorative divider */}
          <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', justifyContent:'center', margin:'1.5rem auto', maxWidth:220 }}>
            <div style={{ flex:1, height:'0.5px', background:`linear-gradient(to right, transparent, ${t.inkFaint})` }}/>
            <svg width="16" height="16" viewBox="0 0 16 16">
              <path d="M8 1 L9.2 6.2 L14.5 5 L10.5 8.5 L13 13.5 L8 10.5 L3 13.5 L5.5 8.5 L1.5 5 L6.8 6.2 Z" fill={t.star} stroke="none"/>
            </svg>
            <div style={{ flex:1, height:'0.5px', background:`linear-gradient(to left, transparent, ${t.inkFaint})` }}/>
          </div>

          {/* Date & venue */}
          {wedding?.wedding_date && (
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:t.inkMid, margin:'0 0 0.3rem', letterSpacing:'0.04em' }}>
              {new Date(wedding.wedding_date).toLocaleDateString('en-GB',{ weekday:'long', day:'numeric', month:'long', year:'numeric' })}
            </p>
          )}
          {wedding?.venue && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:t.inkFaint, margin:0 }}>{wedding.venue}{wedding.city ? `, ${wedding.city}` : ''}</p>}

          {/* Events */}
          {events?.length > 0 && (
            <div style={{ marginTop:'1.5rem', display:'flex', gap:'0.4rem', justifyContent:'center', flexWrap:'wrap' }}>
              {events.map(ev => (
                <div key={ev.id} style={{ background:t.tagBg, border:`1px solid ${t.tagBorder}`, borderRadius:100, padding:'0.3rem 0.875rem' }}>
                  <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:t.tagInk, fontWeight:600 }}>{ev.type}</span>
                </div>
              ))}
            </div>
          )}

          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.55rem', color:t.inkFaint, margin:'2rem 0 0', letterSpacing:'0.12em', textTransform:'uppercase' }}>Made with Okoiya</p>
        </div>
      </div>

      {/* Style switcher */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'0.75rem', marginTop:'0.875rem' }}>
        <button onClick={()=>pickTheme(themeIdx-1)} aria-label="Previous style" style={{ display:'flex', alignItems:'center', justifyContent:'center', width:30, height:30, borderRadius:'50%', border:'1px solid #E5E0D8', background:'white', cursor:'pointer', color:'#6B6B6B' }}><ChevronLeft size={16}/></button>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          {THEMES.map((th,i)=>(
            <button key={th.id} onClick={()=>pickTheme(i)} title={th.name} aria-label={th.name}
              style={{ width:i===themeIdx?24:10, height:10, borderRadius:100, border:'none', cursor:'pointer', padding:0, background:i===themeIdx?th.swatch:'#E0D8CE', transition:'all 0.25s' }}/>
          ))}
        </div>
        <button onClick={()=>pickTheme(themeIdx+1)} aria-label="Next style" style={{ display:'flex', alignItems:'center', justifyContent:'center', width:30, height:30, borderRadius:'50%', border:'1px solid #E5E0D8', background:'white', cursor:'pointer', color:'#6B6B6B' }}><ChevronRight size={16}/></button>
      </div>
      <p style={{ textAlign:'center', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'0.45rem 0 0' }}>
        Style: <strong style={{ color:'#C4826A' }}>{t.name}</strong> · swipe or tap to change
      </p>
      </div>

      {/* Wedding Date Setting */}
      <div className="card" style={{ padding:'1.25rem' }}>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#1A1A1A', margin:'0 0 0.25rem' }}>📅 Wedding Date</p>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:'0 0 0.875rem', lineHeight:1.6 }}>
          Setting or changing your date updates the countdown, the overview bar, and your guest portal automatically.
        </p>
        <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
          <input type="date" value={editDate} onChange={e=>setEditDate(e.target.value)}
            className="input-field" style={{ flex:'1 1 160px', fontSize:'0.85rem' }}/>
          <button onClick={saveWeddingDate} disabled={!editDate||savingDate} className="btn-primary"
            style={{ display:'flex', alignItems:'center', gap:'0.4rem', flexShrink:0 }}>
            {dateSaved?<><Check size={13}/>Saved!</>:savingDate?'Saving…':'Save Date'}
          </button>
        </div>
      </div>

      {/* Share */}
      <div className="card" style={{ padding:'1.25rem' }}>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#1A1A1A', margin:'0 0 0.5rem' }}>Share your countdown page</p>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#6B6B6B', margin:'0 0 1rem', lineHeight:1.6 }}>
          Send this link to your guests, post it on WhatsApp statuses, or share on Instagram stories to build excitement before your wedding.
        </p>
        <div style={{ display:'flex', gap:'0.625rem', flexWrap:'wrap' }}>
          <button onClick={downloadCard} disabled={downloading} style={{ display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.7rem 1.25rem', fontSize:'0.82rem', flexShrink:0, background:'#1A1A1A', color:'white', border:'none', borderRadius:'0.75rem', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontWeight:600 }}>
            ⬇ {downloading ? 'Saving…' : 'Download Image'}
          </button>

        </div>
      </div>
    </div>
  )
}
